<script lang="ts" setup>
	import type { ListItemDropdownContext } from '@/components/Shared/list-item-dropdown.vue'
	import { useResourcesApi } from '~/services/apis/resources'

	const props = defineProps<{
		isPending?: boolean
		totalPlanValue: number | string
		purchaseId?: string
	}>()
	defineEmits<{
		(event: 'activate'): void
		(event: 'reminder'): void
	}>()

	const { internationalNoFormat } = useCurrency()
	const { getAmountHistories } = useResourcesApi()
	const { custodian_id } = useRoute().params

	const { data, isLoading, refresh } = useApiLoader(async () => await getAmountHistories(custodian_id as string), {
		loadOnMount: false
	})
	const histories = computed<ListItemDropdownContext>(() => {
		const result: ListItemDropdownContext = {}
		data.value?.data.forEach(usage => {
			Object.defineProperty(result, usage.title, {
				value: {
					key: usage.title,
					name: usage.title,
					value: usage.amount,
					active: false,
					history: usage.list.map(h => ({
						label: h.title,
						value: h.amount
					}))
				},
				writable: true,
				enumerable: true
			})
		})
		return result
	})
	onMounted(() => {
		if (!props.isPending) {
			refresh()
		}
	})
</script>

<template>
	<div>
		<my-cover-pending-policy-placeholder
			icon-dir="documents"
			path-class="fill-gray-200"
			title="No Amount Used Yet"
			sub-title="This plan has not been activated yet, activate plan to enjoy full benefits"
			icon-name="receipts"
			:is-pending="isPending || false"
			:use-btn="isPending"
			@activate="$emit('activate')"
			@reminder="$emit('reminder')"
		>
			<template #button-group>
				<div></div>
			</template>
		</my-cover-pending-policy-placeholder>
		<my-cover-policy-label
			v-show="!isPending"
			:label="`This plan provides a value of ₦${internationalNoFormat(histories['yearly value']?.value || 0)}`"
			:icon="{ name: 'receipts', dir: 'documents' }"
		/>
		<template v-if="!isPending">
			<shared-empty-placeholder
				v-if="(!histories || !Object.keys(histories).length) && !isLoading"
				title="No Amount Used Yet"
				title-class="text-base font-medium text-gray-600"
				desc="No expenses have been incurred yet."
				desc-class="text-sm text-gray-500"
				icon-dir="documents"
				icon-type="receipts"
				class="mt-10"
			/>

			<shared-list-item-dropdown
				v-else
				:list-items="histories"
				:loading="isLoading"
				class="mt-10 min-h-[244px] rounded-md border border-purple-50 bg-purple-25 px-3"
			/>
		</template>
	</div>
</template>
